import { useEffect, useRef } from "react";

const useScreenWakeLock = (enabled = true) => {
  const sentinelRef = useRef(null);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !("wakeLock" in navigator)) {
      return undefined;
    }

    let isDisposed = false;

    const releaseWakeLock = async () => {
      if (!sentinelRef.current) return;

      try {
        await sentinelRef.current.release();
      } catch {
        // Ignore release errors; browser may already have released the lock.
      } finally {
        sentinelRef.current = null;
      }
    };

    const requestWakeLock = async () => {
      if (
        isDisposed ||
        !enabledRef.current ||
        document.visibilityState !== "visible" ||
        sentinelRef.current
      ) {
        return;
      }

      try {
        const sentinel = await navigator.wakeLock.request("screen");

        if (isDisposed || !enabledRef.current) {
          await sentinel.release();
          return;
        }

        sentinelRef.current = sentinel;
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
          }
        });
      } catch {
        sentinelRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
        return;
      }

      releaseWakeLock();
    };

    requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isDisposed = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      releaseWakeLock();
    };
  }, [enabled]);
};

export default useScreenWakeLock;
